import { useMemo, useState, useEffect, useCallback } from 'react';
import '../../App.scss';
import { API_BASE } from '../../config/constants';
import { useDocs } from '../items/hooks/HDocs';
import { useUsers } from '../items/hooks/HUsers';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { toBase64, downloadBlobFromUrl, createPdfSubmission, getSubmission, getSubmissionDocuments, uploadSignedDocument } from './Nda';
import { HeaderBarProps } from './interfaces/IHeaderBar';
import { useNavigate } from 'react-router-dom';
import G20LogoGradient from '../../assets/xealed.jpg';

const G20GradientVideo = '/assets/video1.mp4';

/**
 * HeaderBar
 * Renders the top header bar with theme toggle, user info, and navigation.
 * @component
 * @param {HeaderBarProps} props - Props for HeaderBar.
 * @returns {JSX.Element} Header bar component.
 */
const HeaderBar = ({ theme, setTheme, onProfileClick, user, onMenuToggle }: HeaderBarProps) => {
    const navigate = useNavigate();
    const [ndaSubmissionId, setNdaSubmissionId] = useState<number | null>(null);
    const [ndaProcessing, setNdaProcessing] = useState(false);
    const { uploadUserDocumentToAzure } = useDocs();
    const { setUserNda } = useUsers();

    const toggleTheme = () => {
        setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
    };

    // NDA helpers are in components/utils/Nda.tsx

    const userDisplayName =
        user?.name ||
        user?.display_name ||
        [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
        user?.email ||
        user?.user ||
        '';

    const isPendingRole = useMemo(() => {
        const roleId = Number(user?.role_id ?? user?.roleId);
        const roleValue = String(user?.role ?? '').toLowerCase();
        return roleId === 4 || roleValue === '4' || roleValue === 'pending';
    }, [user]);

    const isAdminUser = useMemo(() => {
        const roleId = Number(user?.role_id ?? user?.roleId ?? user?.role ?? -1);
        return roleId === 1;
    }, [user]);

    const currentUserId = useMemo(() => {
        return user?.id ?? user?.user_id ?? user?.userId ?? null;
    }, [user]);

    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });

    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    const finalizeNda = useCallback(
        async (submissionId: number | null) => {
            if (!submissionId) return;
            setNdaProcessing(true);
            try {
                const submission = await getSubmission(API_BASE, submissionId);
                const submissionStatus = submission?.status;
                if (submissionStatus !== 'completed') {
                    console.log('[HeaderBar] finalizeNda: submission not completed yet', { submissionId, submissionStatus });
                    return;
                }

                const docs = await getSubmissionDocuments(API_BASE, submissionId);
                const signedDoc = docs?.documents?.[0];
                if (!signedDoc) throw new Error('Could not retrieve signed NDA document.');

                const signerEmail = String(user?.email ?? user?.user ?? '').trim();
                const signedBlob = await downloadBlobFromUrl(String(signedDoc.url));
                const uploadFileName = `nda_${String(signerEmail).toLowerCase().replace(/[^a-z0-9]+/g, '_')}_${submissionId}.pdf`;
                const saved = await uploadSignedDocument(uploadUserDocumentToAzure, signedBlob, uploadFileName, currentUserId);
                if (!saved) throw new Error('NDA was signed but failed to save to Azure.');

                // Mark NDA flag in users table via API and notify user accordingly
                try {
                    console.log('[HeaderBar] calling setUserNda', { userId: currentUserId });
                    const res = await setUserNda(currentUserId);
                    console.log('[HeaderBar] setUserNda result', res);
                    if (res?.success) {
                        setSnackbar({ open: true, message: 'NDA signed and saved — account updated.', severity: 'success' });
                    } else {
                        console.warn('[HeaderBar] setUserNda did not return success', res);
                        setSnackbar({ open: true, message: 'NDA signed and saved, but failed to update account on server.', severity: 'error' });
                    }
                } catch (err) {
                    console.error('[HeaderBar] setUserNda call failed', err);
                    setSnackbar({ open: true, message: 'NDA signed and saved, but failed to update account on server.', severity: 'error' });
                }

                setNdaSubmissionId(null);
            } catch (err: any) {
                console.error('[HeaderBar] finalizeNda error', err);
                setSnackbar({ open: true, message: err?.message || 'Failed to complete NDA signing flow.', severity: 'error' });
            } finally {
                setNdaProcessing(false);
            }
        },
        [uploadUserDocumentToAzure, currentUserId, setUserNda, user, setSnackbar]
    );

    const handleSignNda = async () => {
        if (!currentUserId) {
            console.log('[HeaderBar] handleSignNda: missing user id');
            setSnackbar({ open: true, message: 'User id is missing. Please re-login and try again.', severity: 'error' });
            return;
        }

        const signerEmail = String(user?.email ?? user?.user ?? '').trim();
        const signerName =
            String(user?.name ?? '').trim() ||
            [String(user?.first_name ?? '').trim(), String(user?.last_name ?? '').trim()].filter(Boolean).join(' ').trim() ||
            signerEmail;

        if (!signerEmail) {
            console.log('[HeaderBar] handleSignNda: missing email');
            setSnackbar({ open: true, message: 'User email is required to sign NDA.', severity: 'error' });
            return;
        }

        setNdaProcessing(true);
        try {
            if (!ndaSubmissionId) {
                const ndaFileResponse = await fetch('/assets/NDA.pdf');
                if (!ndaFileResponse.ok) {
                    throw new Error('NDA.pdf not found in frontend public assets.');
                }

                const ndaBlob = await ndaFileResponse.blob();
                const ndaBase64 = await toBase64(ndaBlob);
                const created = await createPdfSubmission(API_BASE, ndaBase64, signerName, signerEmail);
                const submissionId = Number(created.id ?? created?.submission_id ?? created?.id);
                const embedSrc = created?.submitters?.[0]?.embed_src ?? created?.embed_src;
                setNdaSubmissionId(submissionId);
                if (embedSrc) window.open(embedSrc, '_blank', 'noopener,noreferrer');
                console.log('[HeaderBar] NDA signing session created', { submissionId, embedSrc });
                setSnackbar({ open: true, message: 'NDA signing session created. Complete signature, then return to this window to finish saving.', severity: 'success' });
                return;
            }
            // If we already have a submission id, attempt to finalize (this will also be triggered on window focus)
            await finalizeNda(ndaSubmissionId);
        } catch (err: any) {
            console.error('[HeaderBar] NDA flow error', err);
            setSnackbar({ open: true, message: err?.message || 'Failed to complete NDA signing flow.', severity: 'error' });
        } finally {
            setNdaProcessing(false);
        }
    };

    // Auto-check NDA status when the user returns focus to the window (after signing in new tab)
    useEffect(() => {
        const onFocus = () => {
            if (ndaSubmissionId) {
                console.log('[HeaderBar] window focused — checking NDA status', { ndaSubmissionId });
                finalizeNda(ndaSubmissionId).catch((err) => console.error('[HeaderBar] finalizeNda on focus error', err));
            }
        };

        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [ndaSubmissionId, finalizeNda]);

    return (
        <>
        <header className='header-bar'>
            <video className='header-bar-bg-video' autoPlay loop muted playsInline src={G20GradientVideo} />
            {/* Hamburger button — only visible on mobile */}
            <button className='hamburger-btn' onClick={onMenuToggle} title='Toggle menu' aria-label='Toggle menu'>
                <span />
                <span />
                <span />
            </button>
            <div className='header-back'>
                <button className='menu-button back-button' onClick={() => navigate(-1)} title='Back'>
                    ← Back
                </button>
            </div>
            <div className='header-actions'>
                {userDisplayName && (
                    <button
                        type='button'
                        className='header-user'
                        style={{ marginRight: 16, cursor: 'pointer', background: 'transparent', border: 0, padding: 0 }}
                        title='View Profile'
                        onClick={onProfileClick}
                    >
                        {userDisplayName}
                    </button>
                )}
                {isPendingRole && !Boolean(user?.nda) && !isAdminUser && (
                    <button
                        type='button'
                        className='menu-button'
                        style={{ marginRight: 8 }}
                        onClick={handleSignNda}
                        disabled={ndaProcessing}
                        title='Sign NDA'
                    >
                        {ndaProcessing ? 'Processing NDA...' : 'Sign NDA'}
                    </button>
                )}
                <button
                    onClick={toggleTheme}
                    className='theme-toggle-btn'
                    title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                    {theme === 'dark' ? '🌙' : '☀️'}
                </button>
                <button onClick={() => navigate(0)} className='theme-toggle-btn' title='Refresh'>
                    🌀
                </button>
                <img src={G20LogoGradient} alt='G-20 Logo' className='header-logo' />
            </div>
        </header>

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
            <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                {snackbar.message}
            </Alert>
        </Snackbar>
        </>
    );
};

export default HeaderBar;
